import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Users2, AlertCircle, Loader2, UserPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FrequentCollaboratorsCard — rolls up the current user's outgoing
 * Email-This-Call sends into a manager-facing widget with automatic
 * "silo" detection.
 *
 * A silo is when one agent's calls dominate the emails the manager
 * sends to a specific recipient (≥ 70% share, ≥ 3 emails) AND that
 * agent has an email on file. Surfaced as a red alert callout on the
 * relevant row.
 *
 * Empty-state copy nudges the user to start emailing summaries — the
 * data source is `email_send_log`, which only fills once they use the
 * Email button on the Call Detail page.
 */
const FrequentCollaboratorsCard = ({ days = 90 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Detect whether this widget is mounted inside a tenant portal
  // (`/my-domain/...`) or the master admin dashboard (`/analytics/...`)
  // so the "Loop them in now" navigation stays in the same shell.
  const routePrefix = window.location.pathname.startsWith('/my-domain')
    ? '/my-domain'
    : '';

  const loopInNow = (hint) => {
    if (!hint?.last_call_id || !hint?.agent_email) return;
    // Encode as URL params — the Call Detail page reads them on mount
    // to pop the Email popover with the agent already CC'd.
    const qs = new URLSearchParams({
      cc: hint.agent_email,
      auto_email: '1',
    }).toString();
    navigate(`${routePrefix}/calls/${hint.last_call_id}?${qs}`);
  };

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/analytics/frequent-collaborators?days=${days}&limit=8`, { timeout: 15000 })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  const rows = data?.collaborators || [];
  const totalSends = data?.total_sends || 0;
  const siloCount = rows.filter((r) => r.silo_hint).length;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="frequent-collaborators-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users2 className="w-4 h-4 text-teal-500" />
          Frequent Collaborators
          <span className="ml-auto text-[11px] font-medium text-slate-500">
            Last {days} days · {totalSends} email{totalSends === 1 ? '' : 's'}
          </span>
        </CardTitle>
        {siloCount > 0 && (
          <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 mt-1 flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3 flex-shrink-0" />
            {siloCount} collaboration silo{siloCount === 1 ? '' : 's'} detected — review below
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center" data-testid="collab-empty-state">
            No email summaries sent in the last {days} days yet. Use the{' '}
            <span className="font-semibold">Email</span> button on any Call Detail page
            to start building your collaborator map.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((r) => (
              <div
                key={r.email}
                className="py-2.5"
                data-testid={`collab-row-${r.email}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {r.name || r.email}
                    </p>
                    {r.name && (
                      <p className="text-[11px] text-slate-500 truncate">
                        {r.email}
                        {r.role && (
                          <span className="ml-1 capitalize text-slate-400">
                            · {String(r.role).replace('_', ' ')}
                          </span>
                        )}
                      </p>
                    )}
                    {r.top_agent && (
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {r.unique_agents === 1
                          ? `Only about ${r.top_agent.agent_name || 'ext ' + r.top_agent.agent_id}`
                          : `Mostly about ${r.top_agent.agent_name || 'ext ' + r.top_agent.agent_id} (${Math.round(r.top_agent.share * 100)}%) · ${r.unique_agents} agents total`}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-base font-bold text-slate-900 tabular-nums">
                      {r.sends}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">
                      email{r.sends === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>
                {r.silo_hint && (
                  <div
                    className="mt-2 flex items-start gap-2 bg-rose-50 border-l-2 border-rose-400 rounded-r px-2.5 py-1.5"
                    data-testid={`collab-silo-hint-${r.email}`}
                  >
                    <AlertCircle className="w-3.5 h-3.5 text-rose-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-rose-800 leading-snug">
                        {r.silo_hint.message}
                      </p>
                      {r.silo_hint.last_call_id && (
                        <button
                          type="button"
                          onClick={() => loopInNow(r.silo_hint)}
                          className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-rose-600 hover:bg-rose-700 px-2 py-0.5 rounded-full"
                          data-testid={`collab-loop-in-${r.email}`}
                          title={`Open the last call to ${r.email} with ${r.silo_hint.agent_name} pre-CC'd`}
                        >
                          <UserPlus className="w-3 h-3" />
                          Loop {r.silo_hint.agent_name} in now
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FrequentCollaboratorsCard;
